import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

const POLL_INTERVAL_MS = 30000;

/**
 * Encapsulates notification fetching, polling, read-state management and navigation.
 * Returns everything the UI needs, so components stay purely presentational.
 */
const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/admin/notifications?limit=15');
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = useCallback(async () => {
    setLoading(true);
    try {
      await api.post('/admin/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, []);

  const markOneReadAndNavigate = useCallback(async (notif) => {
    const id = notif.id || notif._id;
    if (!notif.read) {
      await api.post(`/admin/notifications/mark-read/${id}`);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev =>
        prev.map(n => (n.id || n._id) === id ? { ...n, read: true } : n)
      );
    }
    if (notif.link) navigate(notif.link);
  }, [navigate]);

  return {
    notifications,
    unreadCount,
    loading,
    markAllRead,
    markOneReadAndNavigate,
  };
};

export default useNotifications;
